import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle2, CircleHelp, Copy, ExternalLink, Play, RefreshCw, ShieldAlert, ShieldCheck, TriangleAlert } from 'lucide-react';
import { runGoogleSheetSyncDryRun, applyGoogleSheetSyncRun } from '@/lib/api';

const IDENTITY_WARNING_RULES = {
  garment_no_auto_incremented: {
    label: 'Sheet garment number did not map cleanly to an app garment',
    severity: 'blocked',
  },
  product_mapping_missing_create_garment_only: {
    label: 'Sheet garment row cannot map to a valid app garment type',
    severity: 'blocked',
  },
  duplicate_fabric_rows_in_app: {
    label: 'App has duplicate fabric rows for the same garment/fabric identity',
    severity: 'blocked',
  },
  multiple_fabric_candidates_by_sku: {
    label: 'Multiple app fabric rows share this fabric ID',
    severity: 'blocked',
  },
  multiple_fabric_candidates_auto_selected: {
    label: 'Fabric row could match multiple app records',
    severity: 'blocked',
  },
  app_only_garments_not_in_sheet: {
    label: 'App garments are missing from the sheet',
    severity: 'review',
  },
  app_only_fabric_rows_not_in_sheet: {
    label: 'App fabric rows are missing from the sheet',
    severity: 'review',
  },
  fabric_id_mismatch: {
    label: 'Fabric ID differs between sheet and app',
    severity: 'review',
  },
  fabric_created_new_row: {
    label: 'Sheet fabric row has no matching app fabric order',
    severity: 'review',
  },
  alteration_row_linked_to_base_garment: {
    label: 'Fabric row is linked to a base garment instead of a dedicated alteration row',
    severity: 'review',
  },
};

const BUCKETS = ['Blocked', 'Review Needed', 'Identity Safe'];

const STATUS_META = {
  Blocked: {
    icon: ShieldAlert,
    cardClass: 'border-rose-200 bg-rose-50 text-rose-700',
    chipClass: 'border-rose-200 bg-rose-50 text-rose-700',
  },
  'Review Needed': {
    icon: TriangleAlert,
    cardClass: 'border-amber-200 bg-amber-50 text-amber-700',
    chipClass: 'border-amber-200 bg-amber-50 text-amber-700',
  },
  'Identity Safe': {
    icon: ShieldCheck,
    cardClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    chipClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  },
};

const formatValueLabel = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getWarningKey = (warning) => String(warning || '').split(':')[0];

const getChangeDisplayGarmentNo = (change) => {
  const resolvedNo = change?.garment_no || change?.proposed_updates?.garment_no || '—';
  const sourceNo = change?.proposed_updates?.source_garment_no;
  if (sourceNo && sourceNo !== resolvedNo) {
    return `${sourceNo} → ${resolvedNo}`;
  }
  return resolvedNo;
};

const getChangeIdentityReview = (change) => {
  const issues = [];
  let severityRank = -1;

  (change?.warnings || []).forEach((warning) => {
    const rule = IDENTITY_WARNING_RULES[getWarningKey(warning)];
    if (!rule) return;
    issues.push(rule.label);
    severityRank = Math.max(severityRank, rule.severity === 'blocked' ? 1 : 0);
  });

  if (change?.entity_type === 'garment' && change?.action === 'create') {
    issues.push('Sheet garment row has no matching app garment');
    severityRank = Math.max(severityRank, 0);
  }

  if (change?.entity_type === 'garment' && change?.action === 'create_test') {
    issues.push('Sheet test garment row has no matching app test garment');
    severityRank = Math.max(severityRank, 0);
  }

  if (change?.entity_type === 'fabric_order' && change?.action === 'create') {
    issues.push('Sheet fabric row has no matching app fabric order');
    severityRank = Math.max(severityRank, 0);
  }

  if (change?.entity_type === 'fabric_order' && change?.proposed_updates?.garment_no) {
    issues.push('Fabric row is linked to the wrong garment in app');
    severityRank = Math.max(severityRank, 0);
  }

  if (change?.entity_type === 'fabric_order' && change?.proposed_updates?.fabric_type) {
    issues.push('Fabric type differs between sheet and app');
    severityRank = Math.max(severityRank, 0);
  }

  const uniqueIssues = Array.from(new Set(issues));
  return {
    isIdentityIssue: uniqueIssues.length > 0,
    severity: severityRank >= 1 ? 'blocked' : uniqueIssues.length > 0 ? 'review' : 'none',
    issues: uniqueIssues,
  };
};

const getOrderReviewStats = (order) => {
  const changes = order?.changes || [];
  const identityChanges = changes.filter((change) => getChangeIdentityReview(change).isIdentityIssue);
  const garmentIdentity = identityChanges.filter((change) => change.entity_type === 'garment');
  const fabricIdentity = identityChanges.filter((change) => change.entity_type === 'fabric_order');
  const actionable = changes.filter((change) => (change?.action || '') !== 'no_change');
  const softDiffs = actionable.filter((change) => !getChangeIdentityReview(change).isIdentityIssue);
  const blockedIdentity = identityChanges.filter((change) => getChangeIdentityReview(change).severity === 'blocked');
  const syncBucket = blockedIdentity.length > 0
    ? 'Blocked'
    : identityChanges.length > 0
      ? 'Review Needed'
      : 'Identity Safe';

  return {
    syncBucket,
    identityIssueCount: identityChanges.length,
    garmentIdentityCount: garmentIdentity.length,
    fabricIdentityCount: fabricIdentity.length,
    softDiffCount: softDiffs.length,
    reviewedCount: changes.length,
  };
};

const getStartingBucket = (orders = []) => {
  const stats = orders.map((order) => getOrderReviewStats(order));
  if (stats.some((item) => item.syncBucket === 'Blocked')) return 'Blocked';
  if (stats.some((item) => item.syncBucket === 'Review Needed')) return 'Review Needed';
  return 'Identity Safe';
};

const getIdentitySummaryRows = (change) => {
  if (change.entity_type === 'garment') {
    return [
      {
        label: 'Sheet garment',
        value: change?.proposed_updates?.source_garment_no || change?.sheet_values?.garment_no || '—',
      },
      {
        label: 'Resolved app garment',
        value: change?.garment_no || change?.proposed_updates?.garment_no || '—',
      },
      {
        label: 'Sheet type',
        value: change?.sheet_values?.product_label || change?.sheet_values?.garment_type || '—',
      },
      {
        label: 'App type',
        value: change?.proposed_updates?.garment_type || change?.current_values?.garment_type || '—',
      },
    ];
  }

  return [
    {
      label: 'Sheet garment',
      value: change?.sheet_values?.garment_no || change?.garment_no || '—',
    },
    {
      label: 'Resolved app garment',
      value: change?.proposed_updates?.garment_no || change?.current_values?.garment_no || change?.garment_no || '—',
    },
    {
      label: 'Sheet fabric ID',
      value: change?.sheet_values?.fabric_sku || change?.proposed_updates?.fabric_sku || '—',
    },
    {
      label: 'App fabric ID',
      value: change?.current_values?.fabric_sku || change?.proposed_updates?.fabric_sku || '—',
    },
    {
      label: 'Sheet fabric type',
      value: change?.sheet_values?.fabric_type || '—',
    },
    {
      label: 'App fabric type',
      value: change?.current_values?.fabric_type || change?.proposed_updates?.fabric_type || '—',
    },
  ];
};

const InfoHint = ({ testId, content }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 transition-colors duration-200 hover:border-zinc-300 hover:text-zinc-900"
        data-testid={testId}
      >
        <CircleHelp className="h-4 w-4" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs bg-zinc-950 text-white">
      <p className="leading-5">{content}</p>
    </TooltipContent>
  </Tooltip>
);

const SummaryCard = ({ bucket, count, detail, testId }) => {
  const meta = STATUS_META[bucket];
  const Icon = meta.icon;
  return (
    <Card className={`border ${meta.cardClass}`} data-testid={testId}>
      <CardContent className="flex items-center justify-between gap-3 px-4 py-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em]">{bucket}</div>
          <div className="mt-1 text-2xl font-semibold leading-none">{count}</div>
          <div className="mt-1 text-[11px] opacity-70">{detail}</div>
        </div>
        <Icon className="h-5 w-5 opacity-80" />
      </CardContent>
    </Card>
  );
};

const FactGrid = ({ rows, testId }) => (
  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3" data-testid={testId}>
    {rows.map((row) => (
      <div key={`${row.label}-${row.value}`} className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{row.label}</div>
        <div className="mt-1 text-sm font-medium text-zinc-900">{formatValueLabel(row.value)}</div>
      </div>
    ))}
  </div>
);

const IssueCard = ({ change }) => {
  const review = getChangeIdentityReview(change);
  const candidateGarments = change?.current_values?.sample_garment_nos || [];
  const candidateFabricRows = change?.current_values?.sample_rows || [];
  const title = change.entity_type === 'garment'
    ? getChangeDisplayGarmentNo(change)
    : `${change?.sheet_values?.garment_no || change?.garment_no || '—'} · ${change?.sheet_values?.fabric_sku || change?.proposed_updates?.fabric_sku || '—'}`;

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm" data-testid={`sync-issue-card-${change.change_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={review.severity === 'blocked' ? STATUS_META.Blocked.chipClass : STATUS_META['Review Needed'].chipClass} data-testid={`sync-change-action-${change.change_id}`}>
              {review.severity === 'blocked' ? 'Blocked' : 'Review Needed'}
            </Badge>
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{change.entity_type === 'garment' ? 'Garment' : 'Fabric'}</span>
          </div>
          <div className="mt-2 text-base font-semibold text-zinc-950" data-testid={`sync-change-title-${change.change_id}`}>{title}</div>
        </div>
        <div className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-600" data-testid={`sync-change-source-row-${change.change_id}`}>
          Sheet row {change.source_row || 'App only'}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2" data-testid={`sync-change-issues-${change.change_id}`}>
        {review.issues.map((issue) => (
          <span key={`${change.change_id}-${issue}`} className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-700">
            {issue}
          </span>
        ))}
      </div>

      <div className="mt-3">
        <FactGrid rows={getIdentitySummaryRows(change)} testId={`sync-change-facts-${change.change_id}`} />
      </div>

      {candidateGarments.length > 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600" data-testid={`sync-change-candidates-${change.change_id}`}>
          App garments to compare: {candidateGarments.join(', ')}
        </div>
      )}

      {candidateFabricRows.length > 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2 text-xs text-zinc-600" data-testid={`sync-change-fabric-candidates-${change.change_id}`}>
          App fabric rows: {candidateFabricRows.map((row) => `${row.garment_no || '—'} / ${row.fabric_type || '—'} / ${row.fabric_sku || '—'}`).join(' • ')}
        </div>
      )}
    </div>
  );
};

export default function GoogleSheetSyncPage() {
  const [sheetUrl, setSheetUrl] = useState('https://docs.google.com/spreadsheets/d/1GtbBtiGtzp5BA2qNqfLCrY97fJcRywKlbNSVWS47Zkg/edit?gid=0#gid=0');
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [runData, setRunData] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [activeBucket, setActiveBucket] = useState('Blocked');

  const orderReviewMap = useMemo(() => {
    const next = {};
    (runData?.orders || []).forEach((order) => {
      next[order.order_id] = getOrderReviewStats(order);
    });
    return next;
  }, [runData]);

  const bucketCounts = useMemo(() => ({
    Blocked: Object.values(orderReviewMap).filter((stats) => stats.syncBucket === 'Blocked').length,
    'Review Needed': Object.values(orderReviewMap).filter((stats) => stats.syncBucket === 'Review Needed').length,
    'Identity Safe': Object.values(orderReviewMap).filter((stats) => stats.syncBucket === 'Identity Safe').length,
  }), [orderReviewMap]);

  const filteredOrders = useMemo(() => {
    const all = runData?.orders || [];
    const term = orderSearch.trim().toLowerCase();
    const searched = !term
      ? all
      : all.filter((order) => String(order.order_number || '').toLowerCase().includes(term)
        || String(order.customer_name || '').toLowerCase().includes(term));

    return searched
      .filter((order) => (orderReviewMap[order.order_id]?.syncBucket || 'Blocked') === activeBucket)
      .sort((left, right) => {
        const leftStats = orderReviewMap[left.order_id] || getOrderReviewStats(left);
        const rightStats = orderReviewMap[right.order_id] || getOrderReviewStats(right);
        const issueDiff = rightStats.identityIssueCount - leftStats.identityIssueCount;
        if (issueDiff !== 0) return issueDiff;
        return String(left.order_number || '').localeCompare(String(right.order_number || ''));
      });
  }, [runData?.orders, orderSearch, activeBucket, orderReviewMap]);

  useEffect(() => {
    if (!filteredOrders.length) {
      setSelectedOrderId(null);
      return;
    }
    if (!filteredOrders.some((order) => order.order_id === selectedOrderId)) {
      setSelectedOrderId(filteredOrders[0].order_id);
    }
  }, [filteredOrders, selectedOrderId]);

  const selectedOrder = useMemo(
    () => (runData?.orders || []).find((order) => order.order_id === selectedOrderId) || null,
    [runData?.orders, selectedOrderId]
  );

  const selectedOrderStats = selectedOrder ? (orderReviewMap[selectedOrder.order_id] || getOrderReviewStats(selectedOrder)) : null;
  const garmentIssues = selectedOrder?.changes?.filter((change) => change.entity_type === 'garment' && getChangeIdentityReview(change).isIdentityIssue) || [];
  const fabricIssues = selectedOrder?.changes?.filter((change) => change.entity_type === 'fabric_order' && getChangeIdentityReview(change).isIdentityIssue) || [];
  const hiddenSoftDiffs = selectedOrder?.changes?.filter((change) => (change?.action || '') !== 'no_change' && !getChangeIdentityReview(change).isIdentityIssue) || [];

  const selectedOrderIds = useMemo(
    () => Object.entries(selectedOrders).filter(([, checked]) => checked).map(([orderId]) => orderId),
    [selectedOrders]
  );

  const runStats = useMemo(() => {
    const orders = runData?.orders || [];
    const reviewedRows = orders.flatMap((order) => order.changes || []).length;
    const hiddenSoftRows = Object.values(orderReviewMap).reduce((sum, stats) => sum + (stats.softDiffCount || 0), 0);
    return {
      reviewedRows,
      hiddenSoftRows,
      blockedOrders: bucketCounts.Blocked,
      reviewOrders: bucketCounts['Review Needed'],
      identitySafeOrders: bucketCounts['Identity Safe'],
      totalOrders: orders.length,
    };
  }, [runData, orderReviewMap, bucketCounts]);

  const runDrySync = async () => {
    if (!sheetUrl.trim()) {
      toast.error('Please provide Google Sheet URL');
      return;
    }

    setDryRunLoading(true);
    try {
      const response = await runGoogleSheetSyncDryRun({ sheet_url: sheetUrl.trim() });
      const payload = response?.data;
      setRunData(payload);
      setSelectedOrders({});
      const startingBucket = getStartingBucket(payload?.orders || []);
      setActiveBucket(startingBucket);
      const firstOrder = (payload?.orders || []).find((order) => getOrderReviewStats(order).syncBucket === startingBucket);
      setSelectedOrderId(firstOrder?.order_id || null);
      const reviewedRows = (payload?.orders || []).flatMap((order) => order.changes || []).length;
      toast.success(`Dry run complete: ${reviewedRows} reviewed rows across ${(payload?.orders || []).length} orders`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run dry sync');
    } finally {
      setDryRunLoading(false);
    }
  };

  const setAllOrderSelection = (checked) => {
    const safeOrders = (runData?.orders || []).filter((order) => (orderReviewMap[order.order_id]?.syncBucket || 'Blocked') === 'Identity Safe');
    const next = {};
    safeOrders.forEach((order) => {
      next[order.order_id] = checked;
    });
    setSelectedOrders(next);
  };

  const applySelected = async () => {
    if (!runData?.run_id) {
      toast.error('Run dry sync first');
      return;
    }
    if (selectedOrderIds.length === 0) {
      toast.error('Select at least one identity-safe order');
      return;
    }

    setApplyLoading(true);
    try {
      const response = await applyGoogleSheetSyncRun({
        run_id: runData.run_id,
        selected_order_ids: selectedOrderIds,
        overrides: {},
      });
      const result = response?.data?.result || {};
      toast.success(`Applied ${result.applied_changes || 0} updates, created ${result.created_records || 0}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply sync');
    } finally {
      setApplyLoading(false);
    }
  };

  const copyOrderNumber = async (orderNumber) => {
    if (!orderNumber) return;
    try {
      let copied = false;
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(orderNumber);
          copied = true;
        } catch (error) {
          copied = false;
        }
      }

      if (!copied) {
        const textarea = document.createElement('textarea');
        textarea.value = orderNumber;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        const fallbackCopied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!fallbackCopied) {
          throw new Error('Fallback copy failed');
        }
      }
      toast.success(`Copied ${orderNumber}`);
    } catch (error) {
      toast.error('Failed to copy order number');
    }
  };

  const openEditOrderPage = (orderId) => {
    if (!orderId) return;
    window.open(`/orders/${orderId}/edit`, '_blank', 'noopener,noreferrer');
  };

  return (
    <TooltipProvider>
      <div className="flex min-h-[calc(100vh-6rem)] flex-col gap-3 bg-zinc-100/80" data-testid="google-sheet-sync-page">
        <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm" data-testid="google-sheet-sync-header">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-semibold tracking-tight text-zinc-950">Production Data Sync</h1>
                  <InfoHint
                    testId="google-sheet-sync-header-info-button"
                    content="Run a dry sync, review only garment and fabric identity issues first, fix the Google Sheet externally, then rerun until orders become Identity Safe."
                  />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                  <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="google-sheet-sync-safe-badge">Review-only</Badge>
                  {runData?.run_id ? <span data-testid="google-sheet-sync-run-id">Run {runData.run_id}</span> : <span data-testid="google-sheet-sync-run-id">No dry run yet</span>}
                </div>
              </div>
            </div>

            <div className="flex min-w-[280px] flex-1 flex-wrap items-center justify-end gap-2">
              <div className="min-w-[260px] flex-1 max-w-[520px]" data-testid="google-sheet-sync-url-wrapper">
                <Input
                  value={sheetUrl}
                  onChange={(event) => setSheetUrl(event.target.value)}
                  placeholder="Google Sheet URL"
                  className="h-10 border-zinc-200 bg-zinc-50 text-sm"
                  data-testid="google-sheet-sync-url-input"
                />
              </div>
              <Button onClick={runDrySync} disabled={dryRunLoading} className="h-10 rounded-full" data-testid="google-sheet-sync-dry-run-button">
                {dryRunLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                Run Dry Sync
              </Button>
              <Button variant="outline" onClick={() => setAllOrderSelection(true)} disabled={!runData?.orders?.length} className="h-10 rounded-full" data-testid="google-sheet-sync-select-all-button">
                Select All Safe
              </Button>
              <Button variant="outline" onClick={() => setAllOrderSelection(false)} disabled={!runData?.orders?.length} className="h-10 rounded-full" data-testid="google-sheet-sync-deselect-all-button">
                Clear
              </Button>
              <Button onClick={applySelected} disabled={applyLoading || !runData?.run_id || selectedOrderIds.length === 0} className="h-10 rounded-full" data-testid="google-sheet-sync-apply-button">
                {applyLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                Apply Safe ({selectedOrderIds.length})
              </Button>
            </div>
          </div>
        </div>

        {runData && (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="google-sheet-sync-summary-row">
            <SummaryCard bucket="Blocked" count={runStats.blockedOrders} detail="Needs sheet cleanup before sync" testId="sync-summary-blocked-orders" />
            <SummaryCard bucket="Review Needed" count={runStats.reviewOrders} detail="Likely mismatch, verify in sheet" testId="sync-summary-review-orders" />
            <SummaryCard bucket="Identity Safe" count={runStats.identitySafeOrders} detail="Ready for low-priority sync" testId="sync-summary-identity-safe-orders" />
            <Card className="border-zinc-200 bg-white" data-testid="sync-summary-total-orders">
              <CardContent className="flex items-center justify-between gap-3 px-4 py-3">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Run totals</div>
                  <div className="mt-1 text-2xl font-semibold leading-none text-zinc-950">{runStats.totalOrders}</div>
                  <div className="mt-1 text-xs text-zinc-500">{runStats.hiddenSoftRows} hidden soft diffs · {runStats.reviewedRows} reviewed rows</div>
                </div>
                <InfoHint
                  testId="google-sheet-sync-summary-info-button"
                  content="Soft differences like status, dates, quantities, and supplier values stay hidden during this pass. Focus only on garment and fabric identity until orders become Identity Safe."
                />
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid flex-1 gap-3 overflow-hidden xl:grid-cols-[370px_minmax(0,1fr)]" data-testid="google-sheet-sync-workspace">
          <Card className="flex min-h-0 flex-col border-zinc-200 bg-white shadow-sm" data-testid="google-sheet-sync-order-list-card">
            <CardContent className="flex min-h-0 flex-1 flex-col p-3">
              <div className="flex flex-wrap items-center justify-between gap-2 pb-3">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-zinc-900">Order triage</div>
                  <InfoHint
                    testId="google-sheet-sync-order-list-info-button"
                    content="Only Blocked, Review Needed, and Identity Safe buckets are shown. Reviewers fix the Google Sheet outside the app, rerun the dry sync, and then apply only Identity Safe orders."
                  />
                </div>
                <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-700" data-testid="google-sheet-sync-order-search-count">
                  {filteredOrders.length} shown
                </Badge>
              </div>

              <Tabs value={activeBucket} onValueChange={setActiveBucket} className="min-h-0 flex-1">
                <TabsList className="grid h-10 grid-cols-3 rounded-2xl bg-zinc-100 p-1" data-testid="google-sheet-sync-bucket-tabs">
                  {BUCKETS.map((bucket) => (
                    <TabsTrigger key={bucket} value={bucket} className="rounded-xl text-xs font-semibold" data-testid={`google-sheet-sync-bucket-tab-${bucket.toLowerCase().replace(/\s+/g, '-')}`}>
                      {bucket} ({bucketCounts[bucket] || 0})
                    </TabsTrigger>
                  ))}
                </TabsList>

                <div className="mt-3">
                  <Input
                    value={orderSearch}
                    onChange={(event) => setOrderSearch(event.target.value)}
                    placeholder="Search order or customer"
                    className="h-9 border-zinc-200 bg-zinc-50"
                    data-testid="google-sheet-sync-order-search-input"
                  />
                </div>

                <ScrollArea className="mt-3 h-[calc(100vh-21rem)] pr-2" data-testid="google-sheet-sync-order-list-scroll-area">
                  <div className="space-y-2">
                    {filteredOrders.map((order) => {
                      const stats = orderReviewMap[order.order_id] || getOrderReviewStats(order);
                      const isSelected = order.order_id === selectedOrderId;
                      const isSelectable = stats.syncBucket === 'Identity Safe';

                      return (
                        <div
                          key={order.order_id}
                          className={`rounded-2xl border p-2.5 transition-colors duration-200 ${isSelected ? 'border-zinc-900 bg-zinc-950 text-white' : 'border-zinc-200 bg-white hover:bg-zinc-50'}`}
                          data-testid={`sync-order-card-${order.order_id}`}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={!!selectedOrders[order.order_id]}
                              disabled={!isSelectable}
                              onCheckedChange={(checked) => setSelectedOrders((prev) => ({ ...prev, [order.order_id]: !!checked }))}
                              data-testid={`sync-order-select-${order.order_id}`}
                            />
                            <button
                              type="button"
                              className="flex-1 text-left"
                              onClick={() => setSelectedOrderId(order.order_id)}
                              data-testid={`sync-order-row-${order.order_id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <div className="font-semibold" data-testid={`sync-order-number-${order.order_id}`}>{order.order_number}</div>
                                  <div className={`text-xs ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>{order.customer_name || '—'}</div>
                                </div>
                                <Badge className={`${STATUS_META[stats.syncBucket].chipClass} ${isSelected ? 'bg-white/10 text-white border-white/20' : ''}`} data-testid={`sync-order-bucket-${order.order_id}`}>
                                  {stats.syncBucket}
                                </Badge>
                              </div>

                              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                <div className={`rounded-full border px-2.5 py-1 ${isSelected ? 'border-white/10 bg-white/5 text-zinc-200' : 'border-zinc-200 bg-zinc-50 text-zinc-700'}`} data-testid={`sync-order-garment-identity-count-${order.order_id}`}>
                                  G {stats.garmentIdentityCount}
                                </div>
                                <div className={`rounded-full border px-2.5 py-1 ${isSelected ? 'border-white/10 bg-white/5 text-zinc-200' : 'border-zinc-200 bg-zinc-50 text-zinc-700'}`} data-testid={`sync-order-fabric-identity-count-${order.order_id}`}>
                                  F {stats.fabricIdentityCount}
                                </div>
                                <div className={`rounded-full border px-2.5 py-1 ${isSelected ? 'border-white/10 bg-white/5 text-zinc-200' : 'border-zinc-200 bg-zinc-50 text-zinc-700'}`} data-testid={`sync-order-soft-diff-count-${order.order_id}`}>
                                  Hidden {stats.softDiffCount}
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>
                      );
                    })}

                    {filteredOrders.length === 0 && (
                      <div className="rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 px-4 py-8 text-sm text-zinc-500" data-testid="google-sheet-sync-order-search-empty">
                        No orders in this bucket match your search.
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </Tabs>
            </CardContent>
          </Card>

          <Card className="flex min-h-0 flex-col border-zinc-200 bg-white shadow-sm" data-testid="google-sheet-sync-detail-card">
            <CardContent className="flex min-h-0 flex-1 flex-col p-3">
              {!runData ? (
                <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-zinc-300 bg-zinc-50 text-center" data-testid="google-sheet-sync-empty-state">
                  <div className="text-lg font-semibold text-zinc-900">Run a dry sync to start review</div>
                  <div className="mt-2 max-w-md text-sm text-zinc-500">
                    The workspace will group each order into Blocked, Review Needed, or Identity Safe so your team can clean garment and fabric identity first.
                  </div>
                </div>
              ) : !selectedOrder ? (
                <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-dashed border-zinc-300 bg-zinc-50 text-center" data-testid="google-sheet-sync-no-order-selected">
                  <div className="text-lg font-semibold text-zinc-900">Pick an order to review</div>
                  <div className="mt-2 text-sm text-zinc-500">Select an order from the left list to inspect garment and fabric identity issues.</div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-200 pb-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-semibold tracking-tight text-zinc-950" data-testid="google-sheet-sync-selected-order-title">{selectedOrder.order_number}</h2>
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-zinc-500 transition-colors duration-200 hover:border-zinc-300 hover:text-zinc-900"
                          onClick={() => copyOrderNumber(selectedOrder.order_number)}
                          data-testid="google-sheet-sync-selected-order-copy-button"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-200 bg-zinc-50 text-zinc-500 transition-colors duration-200 hover:border-zinc-300 hover:text-zinc-900"
                          onClick={() => openEditOrderPage(selectedOrder.order_id)}
                          data-testid="google-sheet-sync-selected-order-edit-link-button"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        <Badge className={STATUS_META[selectedOrderStats.syncBucket].chipClass} data-testid="google-sheet-sync-selected-order-bucket">{selectedOrderStats.syncBucket}</Badge>
                      </div>
                      <div className="mt-1 text-sm text-zinc-500" data-testid="google-sheet-sync-selected-order-customer">{selectedOrder.customer_name || '—'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs text-zinc-600" data-testid="google-sheet-sync-selected-order-hidden-diffs">
                        {selectedOrderStats.softDiffCount} hidden soft diffs
                      </div>
                      <InfoHint
                        testId="google-sheet-sync-selected-order-info-button"
                        content="Garment and fabric identity are shown here first. Statuses, dates, quantities, and supplier details remain hidden until this order becomes Identity Safe."
                      />
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-3" data-testid="google-sheet-sync-selected-order-stats">
                    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-3">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Garment identity</div>
                      <div className="mt-1 text-2xl font-semibold text-zinc-950">{selectedOrderStats.garmentIdentityCount}</div>
                    </div>
                    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-3">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Fabric identity</div>
                      <div className="mt-1 text-2xl font-semibold text-zinc-950">{selectedOrderStats.fabricIdentityCount}</div>
                    </div>
                    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-3">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Hidden for later</div>
                      <div className="mt-1 text-2xl font-semibold text-zinc-950">{selectedOrderStats.softDiffCount}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:gap-3">
                    <div className="min-h-0 rounded-3xl border border-zinc-200 bg-zinc-50/80 p-3" data-testid="google-sheet-sync-garment-panel">
                      <div className="flex items-center justify-between gap-2 pb-3">
                        <div className="text-sm font-semibold text-zinc-900">Garment Identity Review</div>
                        <Badge variant="outline" className="border-zinc-200 bg-white text-zinc-700" data-testid="google-sheet-sync-garment-panel-count">{garmentIssues.length} issue{garmentIssues.length === 1 ? '' : 's'}</Badge>
                      </div>
                      <ScrollArea className="h-[calc(100vh-24rem)] pr-2" data-testid="google-sheet-sync-garment-panel-scroll-area">
                        <div className="space-y-3">
                          {garmentIssues.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-zinc-300 bg-white px-4 py-8 text-sm text-zinc-500" data-testid="google-sheet-sync-garment-empty">
                              No garment identity blockers in this order.
                            </div>
                          ) : garmentIssues.map((change) => <IssueCard key={change.change_id} change={change} />)}
                        </div>
                      </ScrollArea>
                    </div>

                    <div className="min-h-0 rounded-3xl border border-zinc-200 bg-zinc-50/80 p-3" data-testid="google-sheet-sync-fabric-panel">
                      <div className="flex items-center justify-between gap-2 pb-3">
                        <div className="text-sm font-semibold text-zinc-900">Fabric Identity Review</div>
                        <Badge variant="outline" className="border-zinc-200 bg-white text-zinc-700" data-testid="google-sheet-sync-fabric-panel-count">{fabricIssues.length} issue{fabricIssues.length === 1 ? '' : 's'}</Badge>
                      </div>
                      <ScrollArea className="h-[calc(100vh-24rem)] pr-2" data-testid="google-sheet-sync-fabric-panel-scroll-area">
                        <div className="space-y-3">
                          {fabricIssues.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-zinc-300 bg-white px-4 py-8 text-sm text-zinc-500" data-testid="google-sheet-sync-fabric-empty">
                              No fabric identity blockers in this order.
                            </div>
                          ) : fabricIssues.map((change) => <IssueCard key={change.change_id} change={change} />)}
                        </div>
                      </ScrollArea>
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600" data-testid="google-sheet-sync-hidden-diff-note">
                    <span>{hiddenSoftDiffs.length} low-priority differences hidden for this pass</span>
                    <InfoHint
                      testId="google-sheet-sync-hidden-diff-info-button"
                      content={selectedOrderStats.syncBucket === 'Identity Safe'
                        ? 'This order is identity-safe. Soft differences like statuses, dates, quantity, and supplier values can be synced later in a second pass.'
                        : 'Soft differences stay hidden until garment and fabric identity are coherent. Fix the sheet externally, rerun the dry sync, and revisit this order.'}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
}